/**
 * Утиліта для показу alert повідомлень
 * Використовує Telegram WebApp.showAlert() якщо доступний, інакше звичайний alert()
 * Telegram WebApp.showAlert() не блокує аудіоконтекст браузера
 */

/** Telegram Mini App WebApp surface from @types/telegram-web-app. */
type TelegramWebApp = Window['Telegram']['WebApp'];

/**
 * Отримує Telegram WebApp API (undefined outside Telegram / when SDK missing).
 */
function getTelegramWebApp(): TelegramWebApp | undefined {
  try {
    return window.Telegram?.WebApp;
  } catch {
    return undefined;
  }
}

/**
 * Показує alert повідомлення
 * Використовує Telegram WebApp.showAlert() якщо доступний, інакше звичайний alert()
 * @param message - текст повідомлення
 * @param callback - опціональний callback, який викликається після закриття alert
 */
export function showAlert(message: string, callback?: () => void): void {
  const tg = getTelegramWebApp();
  
  // Перевіряємо чи доступний Telegram WebApp та метод showAlert
  if (tg?.showAlert) {
    try {
      tg.showAlert(message, callback);
      return;
    } catch (error) {
      // Якщо помилка, використовуємо fallback на звичайний alert
      console.debug('[Alert] showAlert failed, using fallback:', error);
    }
  }
  
  // Fallback на звичайний alert
  alert(message);
  resumeAudioContext();
  if (callback) {
    callback();
  }
}

/**
 * Відновлює AudioContext після блокуючих діалогів (alert/confirm)
 * Браузер може призупинити AudioContext, що використовується Howler.js
 */
function resumeAudioContext(): void {
  try {
    const ctx = window.Howler?.ctx;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume();
    }
  } catch { /* silent */ }
}

/**
 * Показує confirm діалог
 * Використовує Telegram WebApp.showConfirm() якщо доступний, інакше звичайний confirm()
 * @param message - текст повідомлення
 * @returns Promise<boolean> - true якщо підтверджено
 */
export function showConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const tg = getTelegramWebApp();
    if (tg?.showConfirm) {
      try {
        tg.showConfirm(message, (confirmed: boolean) => resolve(confirmed));
        return;
      } catch { /* fallback */ }
    }
    // Native fallback — will suspend AudioContext
    const result = confirm(message);
    resumeAudioContext();
    resolve(result);
  });
}

